import React, { forwardRef, useState, useEffect, useCallback, useRef } from 'react'
import { DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd'

import Icon, { IconSize } from 'particles/Icon'
import { TextListProps } from '.'
import NumberInput, { NumberInputProps } from 'particles/NumberInput'
import Dropdown, { DropdownProps } from 'particles/Dropdown'
import IconButton, { ButtonSize } from 'particles/Button/IconButton'
import Text from 'particles/Text'
import TextField, { TextFieldProps } from '../TextField'
import { DraggableBox, DragContainer, RichInfoListContainer } from './style'
import Tooltip from 'particles/Tooltip'
import { RichInfoItemValue } from './RichInfoListGroup'
import styled from 'particles/themes/styled'
import { roundingNumber } from 'particles/utils/number'

const RichInfoDragContainer = styled(DragContainer)`
    align-items: flex-end;
    margin-bottom: 0;
`

interface SizeProps {
    xButton: ButtonSize
    commentIcon: IconSize
    draggableDot: IconSize
}

export const sizeMap: Record<'s' | 'm', SizeProps> = {
    s: {
        xButton: 'xs',
        commentIcon: '16px',
        draggableDot: '16px',
    },
    m: {
        xButton: 's',
        commentIcon: '20px',
        draggableDot: '20px',
    },
}

export type NumberInputPropsWithRounding = NumberInputProps & { useRounding?: boolean }

// conditional types to match props base on type property
// width ratio define proportional width in its container
// we can set which input should be wider by using this in `mainInputData` or `secondaryInputData`
export type InputProps =
    | {
          type: 'number'
          props: NumberInputPropsWithRounding
          widthRatio?: string
          name?: string
      }
    | {
          type: 'dropdown'
          props: DropdownProps
          widthRatio?: string
          name?: string
      }
    | {
          type: 'text'
          props: Partial<TextFieldProps>
          widthRatio?: string
          name?: string
      }

export interface RichInfoListProps
    extends Omit<TextListProps, 'placeholder' | 'name' | 'value' | 'onChange' | 'inputIcon'> {
    mainInputData: InputProps
    secondaryInputData?: InputProps
    value: RichInfoItemValue
    hasError?: boolean
    onChange: (value: RichInfoItemValue) => void
    valueKeys: [string, string]
}

const RichInfoList = forwardRef<
    HTMLDivElement | null,
    RichInfoListProps & {
        draggableProvided?: DraggableProvided
        snapshot?: DraggableStateSnapshot
    }
>(
    (
        {
            width,
            onDelete,
            onChange,
            onCommentClick,
            mainInputData,
            secondaryInputData,
            isDeletable = true,
            error,
            isReadOnly = false,
            draggableProvided,
            snapshot,
            isDraggable = true,
            value,
            hasError = false,
            size = 'm',
            isDisabled = false,
            isCommentable = false,
            isCommented = false,
            tooltipContext,
            isActivated = false,
            className,
            id,
            dataTestId,
            valueKeys,
            ...props
        },
        ref,
    ) => {
        const commentIcon = useRef<HTMLDivElement | null>(null)
        const [isActive, setIsActive] = useState(isActivated)
        const { xButton, commentIcon: commentIconSize, draggableDot: draggableDotSize } = sizeMap[size]

        const handleCommentClick = useCallback(
            (e: React.MouseEvent<HTMLDivElement>): void => {
                if (isCommentable && onCommentClick) {
                    setIsActive(prev => !prev)
                    onCommentClick(e, commentIcon.current)
                }
            },
            [isCommentable, onCommentClick],
        )
        const handleUpdate = (value: RichInfoItemValue) => {
            if (onChange) {
                onChange(value)
            }
        }

        useEffect(() => {
            setIsActive(isActivated)
        }, [isActivated])

        const renderInputElements = (data: InputProps, inputOrder: 0 | 1) => {
            const { props: inputProps, type: inputType } = data
            const generalProps = {
                isReadOnly,
                isDisabled,
            }

            switch (inputType) {
                case 'text': {
                    return (
                        <TextField
                            {...generalProps}
                            {...inputProps}
                            onChange={(inputValue: string) => {
                                if (inputOrder === 0) {
                                    const newValue = {
                                        ...value,
                                        [valueKeys[0]]: inputValue,
                                    }
                                    handleUpdate(newValue)
                                } else {
                                    const newValue = {
                                        ...value,
                                        [valueKeys[1]]: inputValue,
                                    }
                                    handleUpdate(newValue)
                                }
                            }}
                            size={size}
                            minWidth="0px"
                            value={value[valueKeys[inputOrder]] || ''}
                            showErrorStyle={!isDisabled && hasError}
                            inputIcon={inputProps.inputIcon}
                        />
                    )
                }
                case 'number': {
                    const originalValue = value[valueKeys[inputOrder]] || ''
                    const numberValue = inputProps.useRounding ? roundingNumber(originalValue) : originalValue
                    return (
                        <NumberInput
                            {...generalProps}
                            {...inputProps}
                            width="100%"
                            size={size}
                            value={numberValue}
                            showErrorStyle={!isDisabled && hasError}
                            onChange={e => {
                                const inputValue = inputProps.useRounding
                                    ? roundingNumber(e.target.value)
                                    : e.target.value
                                if (inputOrder === 0) {
                                    const newValue = {
                                        ...value,
                                        [valueKeys[0]]: inputValue,
                                    }
                                    handleUpdate(newValue)
                                } else {
                                    const newValue = {
                                        ...value,
                                        [valueKeys[1]]: inputValue,
                                    }
                                    handleUpdate(newValue)
                                }
                            }}
                        />
                    )
                }

                case 'dropdown': {
                    return (
                        <Dropdown
                            {...generalProps}
                            {...inputProps}
                            width="100%"
                            maxWidth="100%"
                            size={size}
                            value={value[valueKeys[inputOrder]]}
                            showErrorStyle={!isDisabled && hasError}
                            onChange={inputValue => {
                                if (inputOrder === 0) {
                                    const newValue = {
                                        ...value,
                                        [valueKeys[0]]: inputValue as string,
                                    }
                                    handleUpdate(newValue)
                                } else {
                                    const newValue = {
                                        ...value,
                                        [valueKeys[1]]: inputValue as string,
                                    }
                                    handleUpdate(newValue)
                                }
                            }}
                        >
                            {inputProps.children}
                        </Dropdown>
                    )
                }

                default: {
                    return null
                }
            }
        }

        return (
            <>
                <RichInfoDragContainer
                    width={width}
                    className={className}
                    isCommentable={isCommentable}
                    isCommented={isCommented}
                    isActive={isActive}
                    ref={ref}
                    isDragging={snapshot?.isDragging}
                    size={size}
                    id={id}
                    data-testid={dataTestId}
                    onClick={e => {
                        if ((e.target as HTMLDivElement).id === id) {
                            handleCommentClick(e)
                        }
                    }}
                    {...props}
                >
                    {draggableProvided && isDraggable && !isReadOnly && !isDisabled ? (
                        <DraggableBox {...draggableProvided.dragHandleProps} className="action-icon">
                            <Icon name="dotsSixVertical" size={draggableDotSize} />
                        </DraggableBox>
                    ) : null}
                    <RichInfoListContainer
                        mainWidthRatio={mainInputData?.widthRatio}
                        secondaryWidthRatio={secondaryInputData?.widthRatio}
                        hasError={hasError}
                        isDisabled={isDisabled}
                    >
                        <div className="main-input">{renderInputElements(mainInputData, 0)}</div>
                        {!!secondaryInputData && (
                            <div className="secondary-input">{renderInputElements(secondaryInputData, 1)}</div>
                        )}
                    </RichInfoListContainer>

                    {/* Right tools: tooltip, delete, comment */}
                    {isDisabled ? (
                        <Tooltip label={tooltipContext?.label} title={tooltipContext?.title}>
                            <IconButton
                                icon="question"
                                size="xs"
                                form="icon"
                                color="neutral"
                                className="action-button"
                            />
                        </Tooltip>
                    ) : null}

                    {isDeletable && !isReadOnly && !isDisabled ? (
                        <IconButton
                            icon="x"
                            form="icon"
                            color="neutral"
                            size={xButton}
                            onClick={onDelete}
                            className="action-button"
                        />
                    ) : null}

                    {isCommentable && !isDisabled ? (
                        <div
                            className="comment-icon action-icon"
                            ref={commentIcon}
                            onClick={e => handleCommentClick(e)}
                        >
                            <Icon
                                name={isCommented ? 'chatText' : 'commentPlus'}
                                size={commentIconSize}
                                color="neutralAction"
                            />
                        </div>
                    ) : null}
                </RichInfoDragContainer>

                {!isDisabled && hasError && !!error && (
                    <Text scale="caption400" color="error" className="error" style={{ marginTop: 0, marginLeft: 4 }}>
                        {error}
                    </Text>
                )}
            </>
        )
    },
)

RichInfoList.displayName = 'RichInfoList'

export default RichInfoList
